import React, { useEffect } from 'react';
import { normalizeLocationIds } from '../locationTypes';
import './LocationAssignmentField.css';

const LocationAssignmentField = ({
  locations = [],
  value = [],
  onChange,
  disabled = false,
  label = 'Locations',
  helperText = 'Select which business locations this applies to.',
  requireAtLeastOne = true,
}) => {
  const selectedIds = normalizeLocationIds(value);
  const activeLocations = locations.filter((location) => location.active !== false);

  useEffect(() => {
    if (disabled || activeLocations.length !== 1 || selectedIds.length > 0) {
      return;
    }

    onChange?.([activeLocations[0].id]);
  }, [activeLocations, disabled, onChange, selectedIds.length]);

  if (activeLocations.length === 0) {
    return null;
  }

  if (activeLocations.length === 1) {
    const location = activeLocations[0];

    return (
      <div className="location-assignment-field">
        <div className="location-assignment-field__header">
          <label className="location-assignment-field__label">{label}</label>
          {helperText ? <p className="location-assignment-field__helper">{helperText}</p> : null}
          <p className="location-assignment-field__helper">
            You have one location, so this applies there automatically. Add another location to choose
            different options per site.
          </p>
        </div>
        <div className="location-assignment-field__options">
          <div className="location-assignment-field__option is-selected location-assignment-field__option--readonly">
            <span className="location-assignment-field__option-text">
              <strong>{location.name}</strong>
              {location.address?.fullAddress ? (
                <span>{location.address.fullAddress}</span>
              ) : null}
            </span>
          </div>
        </div>
      </div>
    );
  }

  const toggleLocation = (locationId) => {
    if (disabled) return;

    const nextIds = selectedIds.includes(locationId)
      ? selectedIds.filter((id) => id !== locationId)
      : [...selectedIds, locationId];

    if (requireAtLeastOne && nextIds.length === 0) {
      return;
    }

    onChange?.(nextIds);
  };

  return (
    <div className="location-assignment-field">
      <div className="location-assignment-field__header">
        <label className="location-assignment-field__label">{label}</label>
        {helperText ? <p className="location-assignment-field__helper">{helperText}</p> : null}
        {requireAtLeastOne ? (
          <p className="location-assignment-field__helper">
            At least one location must stay selected. To remove this from a location, uncheck that
            location and keep another selected.
          </p>
        ) : null}
      </div>
      <div className="location-assignment-field__options">
        {activeLocations.map((location) => {
          const checked = selectedIds.includes(location.id);
          return (
            <label
              key={location.id}
              className={`location-assignment-field__option${checked ? ' is-selected' : ''}`}
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => toggleLocation(location.id)}
              />
              <span className="location-assignment-field__option-text">
                <strong>{location.name}</strong>
                {location.address?.fullAddress ? (
                  <span>{location.address.fullAddress}</span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default LocationAssignmentField;
